import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Backdrop,
  Tooltip as MuiTooltip,
  Autocomplete,
  TextField,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const primaryColor = "#005EB8";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Filter template - same as PDPReport
const filterTemplate = ["primaryskill", "grade", "region", "location", "practice", "departmentname", "proposedbu", "action"];

// Helper to convert key to human readable
const toHuman = (apiKey) =>
  String(apiKey)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// KPI Card colors
const kpiColors = {
  employeeCount: "#E53935",     // Red
  location: "#43A047",          // Green
  region: "#1E88E5",            // Blue
  department: "#8E24AA",        // Purple
  available: "#FB8C00",         // Orange
  proposed: "#00ACC1",          // Cyan
  awip: "#5E35B1",              // Deep Purple
  allocated: "#D81B60",         // Pink
};

// Colors for charts
const CHART_COLORS = [
  "#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00", 
  "#00ACC1", "#5E35B1", "#D81B60", "#FF6384", "#36A2EB",
  "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF"
];

const PIE_COLORS = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF", "#E7E9ED"];
const DOUGHNUT_COLORS = ["#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00", "#00ACC1", "#5E35B1", "#D81B60"];

// KPI Card Component
const KPICard = ({ title, value, color }) => (
  <Paper
    elevation={3}
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 100,
      borderTop: `4px solid ${color}`,
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: 6,
      },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: "center" }}>
      {title}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: "bold", color }}>
      {value}
    </Typography>
  </Paper>
);

// Custom label for pie chart
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.05 ? (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

// Word Cloud Component
const WordCloudComponent = ({ words, title }) => {
  if (!words || words.length === 0) {
    return <Typography color="text.secondary" sx={{ textAlign: "center", mt: 5 }}>No data available</Typography>;
  }
  
  const maxCount = Math.max(...words.map(w => w.count));
  const minCount = Math.min(...words.map(w => w.count));
  
  const shuffledWords = [...words].sort(() => Math.random() - 0.5).slice(0, 50);
  
  return (
    <Box sx={{ 
      display: "flex", 
      flexWrap: "wrap", 
      gap: 0.8,
      justifyContent: "center", 
      alignItems: "center",
      p: 1,
      height: 220,
      overflow: "hidden"
    }}>
      {shuffledWords.map((word, index) => {
        const normalizedValue = minCount === maxCount 
          ? 0.5 
          : (word.count - minCount) / (maxCount - minCount);
        const size = 11 + normalizedValue * 18;
        const color = CHART_COLORS[index % CHART_COLORS.length];
        const rotation = index % 5 === 0 ? -5 : index % 7 === 0 ? 5 : 0;
        
        return (
          <MuiTooltip key={word.text} title={`${word.text}: ${word.count}`} arrow>
            <Typography
              component="span"
              sx={{
                fontSize: `${size}px`,
                fontWeight: normalizedValue > 0.5 ? 700 : normalizedValue > 0.3 ? 600 : 400,
                color,
                cursor: "pointer",
                transform: `rotate(${rotation}deg)`,
                transition: "all 0.2s ease",
                lineHeight: 1.2,
                "&:hover": {
                  transform: `scale(1.15) rotate(${rotation}deg)`,
                  textShadow: "0 0 8px rgba(0,0,0,0.3)",
                },
              }}
            >
              {word.text}
            </Typography>
          </MuiTooltip>
        );
      })}
    </Box>
  );
};

function PDPDashboard() {
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [filters, setFilters] = useState({ location: ["India"] });
  const [distinctValues, setDistinctValues] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  // KPI state
  const [kpis, setKpis] = useState({
    employeeCount: 0,
    location: 0,
    region: 0,
    department: 0,
    available: 0,
    proposed: 0,
    awip: 0,
    allocated: 0,
  });

  // Chart data state
  const [actionData, setActionData] = useState([]);
  const [ageingBucketData, setAgeingBucketData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [accountNameData, setAccountNameData] = useState([]);
  const [marketUnitData, setMarketUnitData] = useState([]);
  const [visibilityData, setVisibilityData] = useState([]);
  const [combinedSkillData, setCombinedSkillData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Compute distinct values for filters
  useEffect(() => {
    if (allData.length === 0) return;
    const distinct = {};
    filterTemplate.forEach((apiKey) => {
      const set = new Set();
      allData.forEach((row) => {
        const val = row[apiKey];
        if (val !== undefined && val !== null && val !== "") {
          set.add(String(val));
        }
      });
      distinct[apiKey] = Array.from(set).sort();
    });
    setDistinctValues(distinct);
  }, [allData]);

  // Apply filters to data
  const filteredData = useMemo(() => {
    if (allData.length === 0) return [];
    return allData.filter((row) => {
      for (const key of filterTemplate) {
        const selected = filters[key];
        if (selected && selected.length > 0) {
          const val = row[key];
          if (!selected.includes(String(val))) return false;
        }
      }
      return true;
    });
  }, [allData, filters]);

  // Recalculate KPIs and charts when filtered data changes
  useEffect(() => {
    if (filteredData.length > 0) {
      calculateKPIs(filteredData);
      prepareChartData(filteredData);
    }
  }, [filteredData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseURI}/api/pool/supplyData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: "pdp" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const resp = await res.json();
      
      // Get the latest date data
      const avail = resp?.availableReportDates ?? [];
      setAvailableDates(avail);
      if (avail.length > 0) {
        const latestDate = avail[0];
        setSelectedDate(latestDate);
        await fetchDataForDate(latestDate);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Error fetching PDP data:", err);
      setLoading(false);
    }
  };

  const fetchDataForDate = async (date) => {
    setLoading(true);
    try {
      const dataRes = await fetch(`${baseURI}/api/pool/supplyData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source: "pdp", reportDate: date }),
      });
      if (dataRes.ok) {
        const dataResp = await dataRes.json();
        const fetchedData = dataResp?.data ?? [];
        setAllData(fetchedData);
        setData(fetchedData);
      }
    } catch (err) {
      console.error("Error fetching PDP data:", err);
    } finally {
      setLoading(false);
    }
  };

  const onDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setFilters({});
    await fetchDataForDate(date);
  };

  const handleFilterChange = (apiKey, value) => {
    setFilters((prev) => ({ ...prev, [apiKey]: value }));
  };

  const findKey = (row, searchTerms) => {
    if (!row) return null;
    const rowKeys = Object.keys(row);
    for (const term of searchTerms) {
      const found = rowKeys.find(k => 
        k.toLowerCase().replace(/[_\s]/g, "").includes(term.toLowerCase().replace(/[_\s]/g, ""))
      );
      if (found) return found;
    }
    return null;
  };

  const calculateKPIs = (data) => {
    if (!data || data.length === 0) return;

    const sampleRow = data[0];
    const employeeIdKey = findKey(sampleRow, ["employeeid"]);
    const locationKey = findKey(sampleRow, ["location"]);
    const regionKey = findKey(sampleRow, ["region"]);
    const departmentKey = findKey(sampleRow, ["departmentname", "department"]);
    const actionKey = findKey(sampleRow, ["action"]);

    // Unique counts
    const uniqueEmployees = new Set(data.map(r => r[employeeIdKey]).filter(Boolean)).size;
    const uniqueLocations = new Set(data.map(r => r[locationKey]).filter(Boolean)).size;
    const uniqueRegions = new Set(data.map(r => r[regionKey]).filter(Boolean)).size;
    const uniqueDepartments = new Set(data.map(r => r[departmentKey]).filter(Boolean)).size;

    // Action counts
    const actionCounts = {};
    data.forEach(row => {
      const action = row[actionKey]?.toString().toLowerCase().trim();
      if (action) {
        actionCounts[action] = (actionCounts[action] || 0) + 1;
      }
    });

    const available = actionCounts["available"] || 0;
    const proposed = actionCounts["proposed"] || 0;
    const awip = actionCounts["awip"] || actionCounts["a-wip"] || 0;
    const allocated = actionCounts["allocated"] || 0;

    setKpis({
      employeeCount: uniqueEmployees,
      location: uniqueLocations,
      region: uniqueRegions,
      department: uniqueDepartments,
      available,
      proposed,
      awip,
      allocated,
    });
  };

  const prepareChartData = (data) => {
    if (!data || data.length === 0) return;

    const sampleRow = data[0];
    const actionKey = findKey(sampleRow, ["action"]);
    const ageingBucketKey = findKey(sampleRow, ["ageingbucket", "ageing_bucket"]);
    const locationKey = findKey(sampleRow, ["location"]);
    const regionKey = findKey(sampleRow, ["region"]);
    const departmentKey = findKey(sampleRow, ["departmentname", "department"]);
    const accountNameKey = findKey(sampleRow, ["releasingaccountname", "accountname"]);
    const marketUnitKey = findKey(sampleRow, ["releasingmarketunit", "marketunit"]);
    const visibilityKey = findKey(sampleRow, ["visibility"]);
    const primarySkillKey = findKey(sampleRow, ["primaryskill"]);
    const secondarySkillKey = findKey(sampleRow, ["secondaryskill"]);

    // Action wise doughnut
    const actionCounts = {};
    data.forEach(row => {
      const action = row[actionKey]?.toString().trim();
      if (action) {
        actionCounts[action] = (actionCounts[action] || 0) + 1;
      }
    });
    setActionData(Object.entries(actionCounts).map(([name, value]) => ({ name, value })));

    // Ageing bucket bar chart
    const ageingCounts = {};
    data.forEach(row => {
      const bucket = row[ageingBucketKey]?.toString().trim();
      if (bucket) {
        ageingCounts[bucket] = (ageingCounts[bucket] || 0) + 1;
      }
    });
    // Sort ageing buckets logically
    const ageingOrder = ["0-30", "31-60", "61-90", "91-120", "121-150", "150+", ">150"];
    const sortedAgeing = Object.entries(ageingCounts)
      .sort((a, b) => {
        const aIdx = ageingOrder.findIndex(o => a[0].includes(o.replace(">", "")) || a[0] === o);
        const bIdx = ageingOrder.findIndex(o => b[0].includes(o.replace(">", "")) || b[0] === o);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        return a[0].localeCompare(b[0]);
      })
      .map(([name, count]) => ({ name, count }));
    setAgeingBucketData(sortedAgeing);

    // Location wise doughnut - top 8
    const locationCounts = {};
    data.forEach(row => {
      const loc = row[locationKey]?.toString().trim();
      if (loc) {
        locationCounts[loc] = (locationCounts[loc] || 0) + 1;
      }
    });
    const topLocations = Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
    setLocationData(topLocations);

    // Region wise pie
    const regionCounts = {};
    data.forEach(row => {
      const reg = row[regionKey]?.toString().trim();
      if (reg) {
        regionCounts[reg] = (regionCounts[reg] || 0) + 1;
      }
    });
    setRegionData(Object.entries(regionCounts).map(([name, value]) => ({ name, value })));

    // Department wise bar chart - top 10
    const deptCounts = {};
    data.forEach(row => {
      const dept = row[departmentKey]?.toString().trim();
      if (dept) {
        deptCounts[dept] = (deptCounts[dept] || 0) + 1;
      }
    });
    const topDepts = Object.entries(deptCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
    setDepartmentData(topDepts);

    // Top 10 releasing account names bar chart
    const accountCounts = {};
    data.forEach(row => {
      const acc = row[accountNameKey]?.toString().trim();
      if (acc) {
        accountCounts[acc] = (accountCounts[acc] || 0) + 1;
      }
    });
    const topAccounts = Object.entries(accountCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
    setAccountNameData(topAccounts);

    // Releasing market unit doughnut
    const muCounts = {};
    data.forEach(row => {
      const mu = row[marketUnitKey]?.toString().trim();
      if (mu) {
        muCounts[mu] = (muCounts[mu] || 0) + 1;
      }
    });
    const topMU = Object.entries(muCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
    setMarketUnitData(topMU);

    // Visibility doughnut
    const visCounts = {};
    data.forEach(row => {
      const vis = row[visibilityKey]?.toString().trim();
      if (vis) {
        visCounts[vis] = (visCounts[vis] || 0) + 1;
      }
    });
    setVisibilityData(Object.entries(visCounts).map(([name, value]) => ({ name, value })));

    // Combined skills word cloud (Primary + Secondary)
    const skillCounts = {};
    data.forEach(row => {
      const primarySkill = row[primarySkillKey]?.toString().trim();
      const secondarySkill = row[secondarySkillKey]?.toString().trim();
      if (primarySkill) {
        skillCounts[primarySkill] = (skillCounts[primarySkill] || 0) + 1;
      }
      if (secondarySkill) {
        skillCounts[secondarySkill] = (skillCounts[secondarySkill] || 0) + 1;
      }
    });
    setCombinedSkillData(
      Object.entries(skillCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 50)
        .map(([text, count]) => ({ text, count }))
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading PDP Dashboard...
          </Typography>
        </Box>
      </Backdrop>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="report-date-label">Report Date</InputLabel>
              <Select
                labelId="report-date-label"
                value={selectedDate || ""}
                label="Report Date"
                onChange={onDateChange}
              >
                {availableDates.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {filterTemplate.slice(0, 6).map((apiKey) => {
            const options = distinctValues[apiKey] ?? [];
            const label = toHuman(apiKey);
            return (
              <Grid item xs={12} sm={6} md={1.5} key={apiKey}>
                <Autocomplete
                  multiple
                  size="small"
                  options={options}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  value={filters[apiKey] ?? []}
                  onChange={(event, newValue) => handleFilterChange(apiKey, newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={label} placeholder={label} />
                  )}
                />
              </Grid>
            );
          })}
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFilters({})}
              sx={{ height: 36 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Total Employees" value={kpis.employeeCount} color={kpiColors.employeeCount} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Locations" value={kpis.location} color={kpiColors.location} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Regions" value={kpis.region} color={kpiColors.region} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Departments" value={kpis.department} color={kpiColors.department} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Available" value={kpis.available} color={kpiColors.available} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Proposed" value={kpis.proposed} color={kpiColors.proposed} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="AWIP" value={kpis.awip} color={kpiColors.awip} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={1.5}>
          <KPICard title="Allocated" value={kpis.allocated} color={kpiColors.allocated} />
        </Grid>
      </Grid>

      {/* Charts Row 1: Action, Ageing Buckets, Visibility */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Action Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={actionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {actionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DOUGHNUT_COLORS[index % DOUGHNUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Ageing Buckets
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={ageingBucketData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={primaryColor}>
                  {ageingBucketData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Visibility
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={visibilityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {visibilityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 2: Location, Region, Market Unit */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Location Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={locationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {locationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DOUGHNUT_COLORS[index % DOUGHNUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Region Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={regionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {regionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Releasing Market Unit
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={marketUnitData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  dataKey="value"
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {marketUnitData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 3: Department, Account Names */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Top 10 Departments
            </Typography>
            <ResponsiveContainer width="100%" height={330}>
              <BarChart data={departmentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill={primaryColor}>
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Top 10 Releasing Account Names
            </Typography>
            <ResponsiveContainer width="100%" height={330}>
              <BarChart data={accountNameData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill={primaryColor}>
                  {accountNameData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DOUGHNUT_COLORS[index % DOUGHNUT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row 4: Combined Skills Word Cloud */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 2, height: 320 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
              Skills Distribution (Primary & Secondary)
            </Typography>
            <WordCloudComponent words={combinedSkillData} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default PDPDashboard;
